import { Injectable, inject } from '@angular/core';
import { SessionStorageService } from '@nequi/design-system-lib/logic';
import Keycloak, { KeycloakLoginOptions } from 'keycloak-js';

import { TrazabilityEntity } from '@private/modules/cash-out-payment/core/entities/trazability.entity';
import { AuthContext } from '@shared/interfaces/auth-context.interface';
import { AuthenticationStrategy } from '@shared/interfaces/authentication-strategies.interface';
import { ExpectedQueryParams } from '@shared/interfaces/expected-query-params.interface';
import { KeycloakErrorHandler } from '@shared/services/keycloak-error-handler/keycloak-error-handler.service';
import { LoginUrlBuilder } from '@shared/services/login-url-builder/login-url-builder.service';
import { NormalInitStrategy } from '@shared/services/normal-init-strategy/normal-init-strategy.service';
import { RedirectCallbackStrategy } from '@shared/services/redirect-callback-strategy/redirect-callback-strategy.service';
import { SessionRestoreStrategy } from '@shared/services/session-restore-strategy/session-restore-strategy.service';
import { parseQueryParams } from '@shared/utils/expected-query-params/expected-query-params';
import { AppInitConfig } from '@shared/utils/keycloak/app-init.config';
import { getKeycloakInstance } from '@shared/utils/keycloak/app.init';

@Injectable({ providedIn: 'root' })
export class KeycloakInitService {
  private readonly sessionStorageService = inject(SessionStorageService);

  public async initialize(): Promise<void> {
    const pendingRedirect = this.sessionStorageService.getItem(AppInitConfig.fwdUrl) as string;

    if (pendingRedirect) {
      return;
    }

    const keycloak: Keycloak = getKeycloakInstance();
    const params: ExpectedQueryParams = parseQueryParams();

    const trazability: TrazabilityEntity = {
      trazabilityCode: params.trazabilityCode ?? '',
      ticketId: params.ticketId ?? '',
    };

    if (params.trazabilityCode && params.ticketId) {
      this.sessionStorageService.setItem(
        AppInitConfig.trazabilityCode,
        trazability,
        AppInitConfig.expiryMinutes
      );
    }

    const loginState =
      (this.sessionStorageService.getItem(AppInitConfig.loginState) as string) ??
      crypto.randomUUID();

    this.sessionStorageService.setItem(
      AppInitConfig.loginState,
      loginState,
      AppInitConfig.expiryMinutes
    );

    const options: KeycloakLoginOptions = {
      redirectUri: new LoginUrlBuilder().build(loginState, trazability),
      locale: AppInitConfig.locale,
    };

    const context: AuthContext = {
      params,
      pathname: window.location.pathname,
      sessionStorage: this.sessionStorageService,
    };

    const strategies: AuthenticationStrategy[] = [
      new SessionRestoreStrategy(),
      new RedirectCallbackStrategy(),
      new NormalInitStrategy(),
    ];

    try {
      for (const strategy of strategies) {
        if (strategy.canHandle(context) && (await strategy.execute(context, options))) {
          return;
        }
      }

      await keycloak.login(options);
    } catch (err) {
      KeycloakErrorHandler.handle(err, AppInitConfig.initialize);
      await keycloak.login(options);
    }
  }
}
